<template>
  <PivotFooterCell
    v-for="cell in rowCells"
    :key="cell.key"
    label
    :text="cell.text"
    :row
    :col="cell.col"
  />
  <PivotFooterCell
    v-for="cell in valueCells"
    :key="cell.key"
    :text="cell.text"
    :raw-value="cell.rawValue"
    :field="cell.field"
    :row
    :col="cell.col"
    :sign-colored="cell.signColored"
    :is-clickable-value="isClickableValue"
    @click="amount => $emit('click:value', cell.dimensions, amount)"
  />
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import { PivotTableClickValueData, PivotTableData, PivotTableValueRenderers } from '@types'

import { CLICKABLE_VALUES_PROVIDE_KEY, GRAND_TOTAL_CAPTION, RENDERERS_PROVIDE_KEY, SIGN_COLORED_FIELDS_PROVIDE_KEY } from '@/components/DS/patterns/Pivot/utils/const'
import { getValuesByColumns, renderValue } from '@/components/DS/patterns/Pivot/utils/query'

import PivotFooterCell from './primitives/PivotFooterCell.vue'

type Props = {
  data: PivotTableData
  columns: PivotTableData[]
  colsList?: string[]
  rowsList?: string[]
  valuesList?: string[]
  /** This footer's own `data-row` — one past the last body row. */
  row: number
}

type Emits = {
  'click:value': [data: PivotTableClickValueData, amount: number]
}

const { data, columns, colsList = [], rowsList = [], valuesList = [], row } = defineProps<Props>()
defineEmits<Emits>()

const renderers = inject(RENDERERS_PROVIDE_KEY, computed<PivotTableValueRenderers>(() => ({})))
const signColoredFields = inject(SIGN_COLORED_FIELDS_PROVIDE_KEY, [])
const isClickableValue = inject(CLICKABLE_VALUES_PROVIDE_KEY, false)

const rowCells = computed(() => rowsList.map((_, index) => ({
  key: `row:${index}`,
  text: index === 0 ? GRAND_TOTAL_CAPTION : '',
  col: index,
})))

type ValueCellSpec = { key: string, text: string, rawValue: unknown, field: string, dimensions: PivotTableClickValueData, signColored: boolean, col: number }

// No row dimensions — the footer is the grand total across every row.
const valueCells = computed<ValueCellSpec[]>(() => {
  const cells: ValueCellSpec[] = []
  const valuesByColumns = getValuesByColumns(data, columns, colsList, valuesList)
  let col = rowsList.length
  for (const { value, valueField, path } of valuesByColumns) {
    // If collapsed, `path` is shorter than `colsList`: map only its present dimensions.
    const colDimensions = Object.fromEntries(colsList.slice(0, path.length - 1).map((colField, index) => [colField, path[index]]))
    cells.push({ key: `col:${path.join('_')}`, text: renderValue(value, valueField, renderers.value), rawValue: value, field: valueField, dimensions: colDimensions, signColored: signColoredFields.includes(valueField), col: col++ })
  }
  for (const field of valuesList) {
    cells.push({ key: `total:${field}`, text: renderValue(data[field], field, renderers.value), rawValue: data[field], field, dimensions: {}, signColored: signColoredFields.includes(field), col: col++ })
  }
  return cells
})
</script>
